import * as React from "react";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Paper,
  Typography,
  Box,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Popper,
  ClickAwayListener,
  MenuList,
  InputAdornment,
  Tooltip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import GetAppIcon from "@material-ui/icons/GetApp";
import { makeStyles } from "@material-ui/core/styles";
import { useNotify } from "react-admin";
import * as XLSX from "xlsx";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";

const useStyles = makeStyles((theme) => ({
  main: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(2),
  },
  card: {
    minWidth: 300,
    marginBottom: theme.spacing(2),
    width: "100%",
    maxWidth: "1600px",
  },
  filtersContainer: {
    padding: theme.spacing(2),
    display: "flex",
    flexWrap: "wrap" as const,
    alignItems: "flex-end",
  },
  filterItem: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    minWidth: 160,
  },
  filterItemNarrow: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(1),
    minWidth: 160,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  table: {
    minWidth: 1200,
  },
  tableHead: {
    backgroundColor: theme.palette.grey[100],
  },
  tableCell: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
  },
  tableCellSmall: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
    maxWidth: 180,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  urlCell: {
    padding: theme.spacing(1),
    fontSize: "0.875rem",
    maxWidth: 200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column" as const,
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
  },
  noDataContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "200px",
    color: theme.palette.grey[500],
  },
}));

const USED_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "false", label: "Not Used" },
  { value: "true", label: "Used" },
];

const formatDate = (dateString: string) => {
  if (!dateString) return "N/A";
  return dateString.replace(/\.\d{3}Z$/, "").replace("T", " ");
};

const formatCurrency = (amount: number | string) => {
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(numAmount);
};

export const LossAbsorbedList = () => {
  const classes = useStyles();
  const notify = useNotify();

  const [brand, setBrand] = useState("");
  const [amountMin, setAmountMin] = useState("");
  const [amountMax, setAmountMax] = useState("");
  const [expiryFrom, setExpiryFrom] = useState("");
  const [expiryTo, setExpiryTo] = useState("");
  const [cancDateFrom, setCancDateFrom] = useState("");
  const [cancDateTo, setCancDateTo] = useState("");
  const [usedFilter, setUsedFilter] = useState("");
  const [downloading, setDownloading] = useState(false);

  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [brandSearch, setBrandSearch] = useState("");
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);
  const brandAnchorRef = useRef<HTMLDivElement>(null);

  const [records, setRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentSkip, setCurrentSkip] = useState(0);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [sortBy, setSortBy] = useState("cancellation_date");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogRecord, setDialogRecord] = useState<any>(null);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogDone, setDialogDone] = useState(false);
  const [dialogResult, setDialogResult] = useState<{ success: boolean; message: string } | null>(null);

  const LIMIT = 20;

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const config = getConfigGet({ fetch_brands: "true" });
        const response = await axios.get<any>(ENDPOINTS.GIFT_CARD_LOSS_ABSORBED, config);
        setBrandOptions(response.data?.brands || []);
      } catch (err) {
        // Silent fail
      }
    };
    fetchBrands();
  }, []);

  const filteredBrands = brandOptions.filter((b) =>
    b.toLowerCase().includes(brandSearch.toLowerCase())
  );

  const buildQueryParams = (skip: number, limit: number = LIMIT) => {
    const params: any = { $limit: limit, $skip: skip };
    if (brand) params.brand = brand;
    if (amountMin) params.amount_min = amountMin;
    if (amountMax) params.amount_max = amountMax;
    if (expiryFrom) params.expiry_from = expiryFrom;
    if (expiryTo) params.expiry_to = expiryTo;
    if (cancDateFrom) params.cancellation_date_from = cancDateFrom;
    if (cancDateTo) params.cancellation_date_to = cancDateTo;
    if (usedFilter) params.used = usedFilter;
    params.sort_by = sortBy;
    params.sort_order = sortOrder;
    return params;
  };

  const fetchRecords = async (skip: number = 0, isLoadMore: boolean = false) => {
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      setRecords([]);
    }

    try {
      const params = buildQueryParams(skip);
      const config = getConfigGet(params);
      const response = await axios.get<any>(ENDPOINTS.GIFT_CARD_LOSS_ABSORBED, config);

      const data = response.data.data || [];
      const total = response.data.total || 0;

      if (isLoadMore) {
        setRecords((prev) => [...prev, ...data]);
      } else {
        setRecords(data);
      }

      setTotalCount(total);
      setCurrentSkip(skip);
      setHasSearched(true);

      const totalLoaded = isLoadMore ? records.length + data.length : data.length;
      setHasMoreData(totalLoaded < total);

      if (data.length === 0 && !isLoadMore) {
        notify("No records found", "info");
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || "Failed to fetch records";
      notify(errorMessage, "error");
      if (!isLoadMore) setRecords([]);
    } finally {
      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    }
  };

  const handleSearch = () => {
    setCurrentSkip(0);
    setHasMoreData(false);
    fetchRecords(0, false);
  };

  const handleLoadMore = () => {
    fetchRecords(currentSkip + LIMIT, true);
  };

  const handleClearFilters = () => {
    setBrand("");
    setBrandSearch("");
    setAmountMin("");
    setAmountMax("");
    setExpiryFrom("");
    setExpiryTo("");
    setCancDateFrom("");
    setCancDateTo("");
    setUsedFilter("");
    setSortBy("cancellation_date");
    setSortOrder("desc");
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      // Fetch all matching rows using current filters for the export.
      const params = buildQueryParams(0, 10000);
      const config = getConfigGet(params);
      const response = await axios.get<any>(ENDPOINTS.GIFT_CARD_LOSS_ABSORBED, config);
      const data = (response.data?.data || []) as any[];

      if (!data.length) {
        notify("No records to download", "info");
        return;
      }

      const rows = data.map((record) => ({
        "User ID": record.user_id ?? "",
        "Client Reference ID": record.client_reference_id ?? "",
        "Gift Card Purchase ID": record.gift_card_purchase_id ?? "",
        Brand: record.brand ?? "",
        Amount: record.amount != null ? Number(record.amount) : "",
        "Purchase Date": record.purchase_date ? formatDate(record.purchase_date) : "",
        "Cancellation Date": record.cancellation_date ? formatDate(record.cancellation_date) : "",
        "Expiry Date": record.expiration_date ? formatDate(record.expiration_date) : "",
        URL: record.url ?? "",
        Status: Number(record.is_used) === 1 ? "Used" : "Not Used",
        "Used By": record.used_by ?? "",
        "Used Date": record.used_at ? formatDate(record.used_at) : "",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Loss Absorbed Vouchers");
      const stamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(wb, `loss_absorbed_vouchers_${stamp}.xlsx`);
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || "Failed to download records";
      notify(errorMessage, "error");
    } finally {
      setDownloading(false);
    }
  };

  const handleSortChange = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const openMarkUsedDialog = (record: any) => {
    setDialogRecord(record);
    setDialogResult(null);
    setDialogDone(false);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    if (dialogDone) {
      fetchRecords(0, false);
    }
  };

  const handleConfirmMarkUsed = async () => {
    if (!dialogRecord) return;
    setDialogLoading(true);

    try {
      const accessToken = localStorage.getItem("accessToken") || "";
      const response = await axios.patch<any>(
        `${ENDPOINTS.GIFT_CARD_LOSS_ABSORBED}/0`,
        {
          client_reference_id: dialogRecord.client_reference_id,
          gift_card_purchase_id: dialogRecord.gift_card_purchase_id,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      setDialogDone(true);
      setDialogResult({
        success: response.data?.success ?? true,
        message: response.data?.message || "Voucher marked as used successfully.",
      });
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err.message || "Failed to mark voucher as used";
      setDialogDone(true);
      setDialogResult({ success: false, message: errorMessage });
    } finally {
      setDialogLoading(false);
    }
  };

  return (
    <div className={classes.main}>
      <Card className={classes.card}>
        <CardContent>
          <Typography variant="h5" component="h1" style={{ marginBottom: 16 }}>
            Loss Absorbed Vouchers
          </Typography>

          {/* Filters Row 1: Brand, Amount range, Used */}
          <div className={classes.filtersContainer}>
            <div ref={brandAnchorRef} style={{ position: "relative" }} className={classes.filterItem}>
              <TextField
                label="Brand"
                value={brand || brandSearch}
                onChange={(e) => {
                  setBrandSearch(e.target.value);
                  setBrand("");
                  setBrandDropdownOpen(true);
                }}
                onFocus={() => setBrandDropdownOpen(true)}
                variant="outlined"
                size="small"
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {brand ? (
                        <span
                          style={{ cursor: "pointer", fontSize: "0.8rem", color: "#999" }}
                          onClick={() => { setBrand(""); setBrandSearch(""); }}
                        >
                          x
                        </span>
                      ) : (
                        <SearchIcon style={{ fontSize: "1rem", color: "#999" }} />
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              <Popper
                open={brandDropdownOpen && filteredBrands.length > 0}
                anchorEl={brandAnchorRef.current}
                placement="bottom-start"
                style={{ zIndex: 1300, width: brandAnchorRef.current?.offsetWidth || 160 }}
              >
                <ClickAwayListener onClickAway={() => setBrandDropdownOpen(false)}>
                  <Paper style={{ maxHeight: 200, overflow: "auto" }}>
                    <MenuList dense>
                      {filteredBrands.map((b) => (
                        <MenuItem
                          key={b}
                          onClick={() => {
                            setBrand(b);
                            setBrandSearch("");
                            setBrandDropdownOpen(false);
                          }}
                          selected={brand === b}
                        >
                          {b}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Paper>
                </ClickAwayListener>
              </Popper>
            </div>

            <TextField
              className={classes.filterItemNarrow}
              label="Amount Min (£)"
              value={amountMin}
              onChange={(e) => setAmountMin(e.target.value)}
              variant="outlined"
              size="small"
            />
            <TextField
              className={classes.filterItemNarrow}
              label="Amount Max (£)"
              value={amountMax}
              onChange={(e) => setAmountMax(e.target.value)}
              variant="outlined"
              size="small"
            />

            <FormControl variant="outlined" size="small" className={classes.filterItem}>
              <InputLabel>Status</InputLabel>
              <Select
                value={usedFilter}
                onChange={(e) => setUsedFilter(e.target.value as string)}
                label="Status"
              >
                {USED_FILTER_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>

          {/* Filters Row 2: Expiry + Cancellation dates + buttons */}
          <div className={classes.filtersContainer}>
            <TextField
              className={classes.filterItem}
              label="Expiry From"
              type="date"
              value={expiryFrom}
              onChange={(e) => setExpiryFrom(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={classes.filterItem}
              label="Expiry To"
              type="date"
              value={expiryTo}
              onChange={(e) => setExpiryTo(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={classes.filterItem}
              label="Cancellation From"
              type="date"
              value={cancDateFrom}
              onChange={(e) => setCancDateFrom(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              className={classes.filterItem}
              label="Cancellation To"
              type="date"
              value={cancDateTo}
              onChange={(e) => setCancDateTo(e.target.value)}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              style={{ marginRight: 8, marginBottom: 8 }}
              startIcon={loading && <CircularProgress size={20} />}
            >
              Search
            </Button>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              style={{ marginBottom: 8 }}
            >
              Clear
            </Button>
          </div>

          {loading && (
            <div className={classes.loadingContainer}>
              <CircularProgress />
              <Typography variant="body2" style={{ marginTop: 16 }}>
                Loading loss absorbed vouchers...
              </Typography>
            </div>
          )}

          {hasSearched && !loading && records.length === 0 && (
            <div className={classes.noDataContainer}>
              <Typography variant="h6">No records found</Typography>
            </div>
          )}

          {records.length > 0 && (
            <>
              <Box style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginTop: 8 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={downloading ? <CircularProgress size={16} /> : <GetAppIcon />}
                  onClick={handleDownload}
                  disabled={downloading || loading}
                >
                  {downloading ? "Preparing..." : "Download"}
                </Button>
              </Box>
              <TableContainer component={Paper} className={classes.tableContainer}>
                <Table className={classes.table} stickyHeader>
                  <TableHead className={classes.tableHead}>
                    <TableRow>
                      <TableCell className={classes.tableCell}>User ID</TableCell>
                      <TableCell className={classes.tableCellSmall}>Client Ref ID</TableCell>
                      <TableCell className={classes.tableCell}>Brand</TableCell>
                      <TableCell className={classes.tableCell} sortDirection={sortBy === "amount" ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === "amount"}
                          direction={sortBy === "amount" ? sortOrder : "desc"}
                          onClick={() => handleSortChange("amount")}
                        >
                          Amount
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={classes.tableCell} sortDirection={sortBy === "purchase_date" ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === "purchase_date"}
                          direction={sortBy === "purchase_date" ? sortOrder : "desc"}
                          onClick={() => handleSortChange("purchase_date")}
                        >
                          Purchase Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={classes.tableCell} sortDirection={sortBy === "cancellation_date" ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === "cancellation_date"}
                          direction={sortBy === "cancellation_date" ? sortOrder : "desc"}
                          onClick={() => handleSortChange("cancellation_date")}
                        >
                          Cancellation Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={classes.tableCell}>URL</TableCell>
                      <TableCell className={classes.tableCell} sortDirection={sortBy === "expiration_date" ? sortOrder : false}>
                        <TableSortLabel
                          active={sortBy === "expiration_date"}
                          direction={sortBy === "expiration_date" ? sortOrder : "desc"}
                          onClick={() => handleSortChange("expiration_date")}
                        >
                          Expiry Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell className={classes.tableCell}>Status</TableCell>
                      <TableCell className={classes.tableCell}>Used Date</TableCell>
                      <TableCell className={classes.tableCell}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {records.map((record, index) => (
                      <TableRow key={record.client_reference_id || index} hover>
                        <TableCell className={classes.tableCell}>
                          {record.user_id || "N/A"}
                        </TableCell>
                        <TableCell
                          className={classes.tableCellSmall}
                          title={record.client_reference_id}
                          style={{ cursor: "pointer" }}
                          onClick={() => {
                            navigator.clipboard.writeText(record.client_reference_id || "");
                            notify("Client Reference ID copied", "info");
                          }}
                        >
                          {record.client_reference_id || "N/A"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {record.brand || "N/A"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {formatCurrency(record.amount)}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {formatDate(record.purchase_date || "")}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {record.cancellation_date ? formatDate(record.cancellation_date) : "N/A"}
                        </TableCell>
                        <TableCell
                          className={classes.urlCell}
                          style={{ cursor: record.url ? "pointer" : "default" }}
                          onClick={() => {
                            if (record.url) {
                              navigator.clipboard.writeText(record.url);
                              notify("URL copied", "info");
                            }
                          }}
                        >
                          {record.url ? (
                            <Tooltip title={record.url} arrow>
                              <span style={{ color: "#1976d2" }}>Copy</span>
                            </Tooltip>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {record.expiration_date ? formatDate(record.expiration_date) : "N/A"}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {Number(record.is_used) === 1 ? (
                            <Tooltip
                              title={
                                record.used_by
                                  ? `Marked by ${record.used_by} on ${formatDate(record.used_at || "")}`
                                  : "Marked as used"
                              }
                              arrow
                            >
                              <Chip
                                icon={<CheckCircleIcon style={{ fontSize: "1rem" }} />}
                                label="Used"
                                size="small"
                                style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", fontSize: "0.75rem" }}
                              />
                            </Tooltip>
                          ) : (
                            <Chip
                              label="Not Used"
                              size="small"
                              style={{ backgroundColor: "#fff3e0", color: "#e65100", fontSize: "0.75rem" }}
                            />
                          )}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {record.used_at ? (
                            <Tooltip
                              title={record.used_by ? `Marked by ${record.used_by}` : ""}
                              arrow
                            >
                              <span>{formatDate(record.used_at)}</span>
                            </Tooltip>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell className={classes.tableCell}>
                          {Number(record.is_used) === 1 ? (
                            <Typography variant="body2" color="textSecondary" style={{ fontSize: "0.75rem" }}>
                              —
                            </Typography>
                          ) : (
                            <Button
                              size="small"
                              variant="contained"
                              color="primary"
                              style={{ fontSize: "0.7rem" }}
                              onClick={() => openMarkUsedDialog(record)}
                            >
                              Mark Used
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 16, marginTop: 16 }}>
                <Typography variant="body1" style={{ fontWeight: 600 }}>
                  {records.length} out of {totalCount}
                </Typography>
                {hasMoreData && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    startIcon={loadingMore ? <CircularProgress size={20} /> : null}
                  >
                    {loadingMore ? "Loading..." : "Load More"}
                  </Button>
                )}
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Mark as Used Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Mark Voucher as Used</DialogTitle>
        <DialogContent>
          {!dialogDone ? (
            dialogRecord && (
              <Box>
                <Box style={{
                  padding: "12px 16px",
                  backgroundColor: "#fff3e0",
                  borderLeft: "4px solid #ff9800",
                  borderRadius: 4,
                  marginBottom: 16,
                }}>
                  <Typography variant="subtitle2" style={{ color: "#e65100", fontWeight: 700, marginBottom: 4 }}>
                    Confirm: Mark this voucher as used
                  </Typography>
                </Box>

                <TableContainer component={Paper} variant="outlined" style={{ marginTop: 8 }}>
                  <Table size="small">
                    <TableBody>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600, width: "35%" }}>User ID</TableCell>
                        <TableCell>{dialogRecord.user_id}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600 }}>Brand</TableCell>
                        <TableCell>{dialogRecord.brand}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600 }}>Amount</TableCell>
                        <TableCell>{formatCurrency(dialogRecord.amount)}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ fontWeight: 600 }}>Client Reference</TableCell>
                        <TableCell style={{ wordBreak: "break-all" }}>{dialogRecord.client_reference_id}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )
          ) : (
            dialogResult && (
              <Box style={{
                padding: "16px",
                backgroundColor: dialogResult.success ? "#e8f5e9" : "#fce4ec",
                borderLeft: `4px solid ${dialogResult.success ? "#4caf50" : "#e53935"}`,
                borderRadius: 4,
              }}>
                <Typography variant="subtitle1" style={{
                  fontWeight: 700,
                  color: dialogResult.success ? "#2e7d32" : "#c62828",
                  marginBottom: 4,
                }}>
                  {dialogResult.success ? "Success" : "Error"}
                </Typography>
                <Typography variant="body2" style={{
                  color: dialogResult.success ? "#33691e" : "#b71c1c",
                }}>
                  {dialogResult.message}
                </Typography>
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions>
          {!dialogDone ? (
            <>
              <Button onClick={closeDialog} disabled={dialogLoading}>
                Cancel
              </Button>
              <Button
                onClick={handleConfirmMarkUsed}
                color="primary"
                variant="contained"
                disabled={dialogLoading}
                startIcon={dialogLoading ? <CircularProgress size={20} /> : null}
              >
                {dialogLoading ? "Processing..." : "Confirm Mark Used"}
              </Button>
            </>
          ) : (
            <Button onClick={closeDialog} color="primary" variant="contained">
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default LossAbsorbedList;
